import type { BaseEntity, SimpleFieldConfig } from "@/components/shared/UniversalCRMTable/types";

export interface FTSEnhancedSearchOptions {
  enableFTS: boolean;
  entityType?: "contacts" | "companies" | "deals" | "tickets" | "tasks" | "communications" | "offerings" | "recurring-transactions" | "recurring-charges" | "admin_users" | "social_media_posts";
  ftsResults?: any[];
  ftsLoading?: boolean;
  ftsError?: string | null;
  searchTerm?: string;
}

/**
 * Utility to enhance search results with FTS relevance ranking
 * Falls back to client-side ILIKE search if FTS is disabled or unavailable
 * 
 * CRITICAL: When FTS is enabled and a search term is present, ONLY return FTS results.
 * This ensures the table displays exactly what the FTS API returned, preventing misalignment
 * between search results and displayed data.
 * 
 * NOTE: FTS results are returned directly from the API and may contain different fields
 * than the full GraphQL objects. The table should handle both full objects and FTS result objects.
 * 
 * This allows entity pages to opt-in to FTS search without breaking existing functionality
 */
export function enhanceSearchWithFTS<TData extends BaseEntity>(
  data: TData[],
  searchTerm: string,
  searchableFields: string[],
  options: FTSEnhancedSearchOptions
): TData[] {
  // If FTS is enabled and we have a search term, use ONLY FTS results
  if (options.enableFTS && searchTerm && searchTerm.trim().length > 0) {
    if (!options.ftsResults || options.ftsResults.length === 0) {
      // FTS returned no results - return empty array
      return [];
    }

    // Return FTS results directly, preserving relevance order
    // FTS results are already ranked by relevance from the API
    // They may contain a subset of fields compared to full GraphQL objects,
    // but they represent the actual search results the user should see
    return options.ftsResults as TData[];
  }

  // If FTS is disabled or no search term, fall back to client-side search
  if (!options.enableFTS || !searchTerm || searchTerm.trim().length === 0) {
    return applyClientSideSearch(data, searchTerm, searchableFields);
  }

  // Fallback: return all data if FTS is enabled but no search term
  return data;
}

/**
 * Client-side ILIKE-style search (existing implementation)
 * Used as fallback when FTS is disabled
 */
function applyClientSideSearch<TData extends BaseEntity>(
  data: TData[],
  searchTerm: string,
  searchableFields: string[]
): TData[] {
  if (!searchTerm || searchTerm.trim().length === 0) {
    return data;
  }

  const lowerSearchTerm = searchTerm.toLowerCase().trim();

  return data.filter((item) => {
    return searchableFields.some((fieldId) => {
      const value = (item as any)[fieldId];
      if (value === null || value === undefined) return false;

      // Handle different value types
      if (typeof value === "string") {
        return value.toLowerCase().includes(lowerSearchTerm);
      } else if (typeof value === "number") {
        return value.toString().includes(lowerSearchTerm);
      } else if (Array.isArray(value)) {
        return value.some((arrayItem) => String(arrayItem).toLowerCase().includes(lowerSearchTerm));
      } else if (typeof value === "object") {
        // Handle related objects
        if (value.name || value.first_name || value.last_name || value.email) {
          const nameFields = [value.name, value.first_name, value.last_name, value.email]
            .filter(Boolean)
            .map((field) => String(field).toLowerCase());
          return nameFields.some((field) => field.includes(lowerSearchTerm));
        }

        if (value.title || value.subject) {
          return String(value.title || value.subject)
            .toLowerCase()
            .includes(lowerSearchTerm);
        }

        if (value.description || value.notes) {
          return String(value.description || value.notes)
            .toLowerCase()
            .includes(lowerSearchTerm);
        }
      }

      return false;
    });
  });
}

/**
 * Comparator for sorting by FTS relevance
 * Used when FTS results need to be sorted by relevance score
 */
export function compareByRelevance(
  a: any,
  b: any,
  direction: "asc" | "desc" = "desc"
): number {
  const relevanceA = a._relevance ?? 0;
  const relevanceB = b._relevance ?? 0;
  
  const comparison = relevanceB - relevanceA; // Higher relevance first by default
  return direction === "asc" ? -comparison : comparison;
}
